/**
 * THE CAPABILITY TEMPLATE LIBRARY — SCREEN_MAP #25, API_CONTRACT §6.1-§6.7.
 *
 * ── WHAT A TEMPLATE IS, SO THE SCREEN SAYS THE RIGHT THING ─────────────────────────────
 *
 * A template is a REUSABLE CAPABILITY STRUCTURE — functional areas, capabilities beneath them, and
 * their weights — that a new capability model can be created FROM, so an initiative starts with a
 * weighted tree instead of a blank page. It is what makes a consultancy's standard model reusable
 * across clients instead of retyped. The library is global to the install, because D-002 settles
 * that there is no tenant to scope it to: *"one client is one install"*.
 *
 * **WHY THIS SCREEN EXISTS NOW.** The New Capability Model panel's "Start from a template" picker
 * offered only "— Start empty —", because the table behind it was empty and nothing in the product
 * could put anything in it. The endpoints had been built and driven; the screen never was. The
 * owner: *"and yes, I need you to build template library of course"*.
 *
 * ── INSTANTIATION COPIES. IT DOES NOT SHARE. ───────────────────────────────────────────
 *
 * Worth stating on this screen because the precedent gets it the other way round: SCREEN_MAP #25
 * notes that APR's framework clone SHARES its dimensions, so editing the clone edits the original.
 * Here that cannot happen — a template line and a capability are different tables with different
 * parents, so a copy is the only thing the DDL permits. Editing a model never reaches the template
 * it came from, and editing a template never reaches a model already built from it.
 *
 * ── TWO VIEWS, ONE URL ────────────────────────────────────────────────────────────────
 *
 * `?template=<id>` opens one template's tree; without it you get the library table. The same
 * mechanism the capability model editor uses for `?model=`, and for the same reason
 * (`FrameworkBuilder.tsx:933-949`): the URL is the single source of truth for what is open, so the
 * load effect that keys on it does the reload and a shared link opens the same template.
 *
 * ── THE TABLE IS D-018's PATTERN AND THE TREE IS THE MODEL EDITOR'S COMPONENT ──────────
 *
 * The list is the Knowledge Library pattern (`../chrome/recordList.css`, prefix `erp-rl-`), which
 * D-018 makes the pattern for *"every screen in this module that presents more than one record"*.
 * Its columns deliberately parallel the capability models table the owner already uses — name with
 * its description beneath, a size, a verdict on the weights, a status badge, the outlined glyph row
 * actions. The size column is headed **Capabilities** for that parallel; the server's field behind
 * it is `line_count`, and like the models table's `capability_count` it counts every row in the
 * tree, functional areas included.
 *
 * The tree is `WeightedTree` — the same component the capability model editor renders, because a
 * template's lines are the same object one step earlier (`0094`: *"Arbitrary depth, same tree shape
 * as capability. A top-level line is a functional area."*) and §6.7 specifies the weight endpoint as
 * *"identical in shape and semantics"* to the model's. One tree, one set of helpers, one
 * implementation of the 100% rule.
 *
 * ── NO BASELINE HERE, AND BALANCE STILL MATTERS ───────────────────────────────────────
 *
 * Nothing is ever scored against a template, so there is no gate and no baseline action. But
 * whether it balances is on the screen in two places — a badge in the table and a band above the
 * tree — because of what §6.8 does with an unbalanced one: it REFUSES to instantiate it. `0094`'s
 * own words, on why a template carries weights at all: *"a template that cannot carry them cannot
 * produce a model that balances to 100.0 on instantiation, and §6.1 forbids redistributing silently
 * to make it balance afterwards."* So an unbalanced template is not an error to be blocked, it is a
 * draft that cannot yet be used — and the screen has to say which.
 *
 * ── THE ONE THING THE SERVER DOES NOT OFFER ───────────────────────────────────────────
 *
 * **There is no weight LOCK on a template line.** `capability_template_line` has no `weight_locked`
 * column (`0094`), so `capabilityTemplates.ts` records that *"there is nothing on a template line to
 * lock and no `weight_locked` refusal here"*. `WeightedTree` is therefore given no lock handler and
 * draws no padlock: Distribute evenly is here, and the third
 * control the model editor has is not, because there is nothing behind it. A session-only lock was
 * considered and rejected — it would look like stored state and vanish on reload.
 *
 * There is also no **Effective** column, for the same kind of reason: the model's read computes
 * `effective_weight_tenths` down the tree and the template's read computes nothing of the sort. §3.3
 * makes that figure the server's, so the screen renders no column rather than deriving one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
/** D-014 — the parent picker chooses a RECORD, so it is the platform's one record-picker field. */
import { SelectField } from '@platform/ui-kit';
import { ErpScreen } from '../ErpScreen';
import { ERP_PROMPT } from '../prompts';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { FormPanel } from '../chrome/FormPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { erpApi, ErpApiError } from '../api';
import {
  type CapabilityTemplate,
  type CapabilityTemplateDetailPayload,
  type CapabilityTemplateLine,
  type CapabilityTemplateLinesPayload,
  type ErpContext,
  type Initiative,
  type WeightBalanceReport,
} from '../types';
import { canEditCapabilityModel, canManageTemplateLibrary } from '../roles';
import { describeKeyProblem, draftKey, finishKey, suggestKeyFromName } from '../keys';
/** The 100% rule is `@platform/erp-scoring`'s, through this module's re-export of it (D-009). */
import {
  describeBalance,
  isWeightInRange,
  orderTree,
  summariseBalance,
  textToTenths,
  type WeightChange,
  type WeightNode,
} from '../weights';
import { WeightedTree, type WeightedTreeRow } from './WeightedTree';
import '../chrome/recordList.css';
import './CapabilityTemplateLibrary.css';

/* ---------------------------------------------------------------------------
 * Contextual help — AUTHORED, because this module has no source app to lift wording from
 * (UI_CONVENTIONS §3.7). The facts come from `0094`, API_CONTRACT §6.1-§6.8 and D-002.
 * ------------------------------------------------------------------------- */

const TemplateLibraryHelp: Help = {
  title: 'Capability Templates',
  icon: '📚',
  purpose:
    'A <strong>template</strong> is a reusable capability structure — functional areas, the capabilities beneath them, and their weights. A new <strong>capability model</strong> can be created from one, so an initiative starts with a weighted tree instead of a blank page. This is where the structures your firm reuses across clients are kept.',
  doHere: [
    '<strong>Create a template</strong> and give it a name and a <strong>key</strong>. The key is how the template is referred to and no two templates may share one.',
    'Open a template and <strong>add functional areas</strong>, then capabilities beneath them. A new row always starts at <strong>0.0%</strong> — nothing is taken from its siblings to pay for it.',
    'Type each <strong>weight</strong> to one decimal place. The running total for that set of siblings updates as you type, and the weight saves when you leave the cell.',
    'Use <strong>Distribute evenly</strong> on a sibling set to give each of them an equal share. It is the only weight helper &mdash; <em>Normalize proportionally</em> was removed because on a tree whose weights are equal (all zero included, which is where every new tree starts) it produced exactly the same numbers.',
    '<strong>Create a capability model</strong> from a template with the \u{1F4CB} action on its row — that is the usual way a model is started, and the template is copied into it.',
    '<strong>Add a capability</strong> with the <strong>+ capability</strong> link on a functional area. It sits on the functional area alone, not on every row beneath it.',
    '<strong>Delete</strong> one only if no model was ever created from it — a model records which template it came from, and removing that row would break the record of what its scores were computed against. There is no Active flag: delete is the only way to retire a template, so one that has been used stays in the list.',
  ],
  fits: 'ERP Consolidation &rarr; Capability Templates',
  firstTime:
    'Create a template, add two or three functional areas, then use Distribute evenly on them — that is the fastest way to one that balances, and you can adjust from there.',
  sections: [
    {
      type: 'defs',
      label: 'Whether it balances',
      items: [
        {
          term: 'Balanced',
          color: '#0d652d',
          def: 'Every <strong>sibling set</strong> totals exactly 100.0% — the functional areas together, and the children of each capability together. A model <strong>can</strong> be created from it.',
        },
        {
          term: 'Does not balance',
          color: '#8c3a00',
          def: 'It still <strong>saves</strong>, and it is a perfectly normal state while you are building one. But a model <strong>cannot</strong> be created from it, because the model it produced could not balance either — and nothing here redistributes weights silently to make the sum come out.',
        },
      ],
      note: 'A template with no rows at all reports as balanced, because there is no set that fails. It is refused at model creation for being empty rather than for being unbalanced.',
    },
    {
      type: 'html',
      label: 'A template is COPIED, never shared',
      html: 'Creating a model from a template copies every row and weight into the model. Editing the model afterwards never touches the template, and editing the template never reaches a model already built from it. That is the opposite of how framework cloning behaves elsewhere in the platform, and it is deliberate.',
    },
    {
      type: 'html',
      label: 'What this screen does not have',
      html: 'There is no <strong>baseline</strong> and no versioning: nothing is ever scored against a template, so there is nothing to freeze. There is also no per-weight <strong>lock</strong> — a template row has no lock to store, so both helpers share out the whole 100%.',
    },
  ],
};

type Toast = { type: 'success' | 'error'; message: string } | null;

/** The attribute form's state — §6.3's four fields, and the id when it is an edit. */
interface AttrForm {
  /** `null` while creating. */
  id: string | null;
  template_key: string;
  name: string;
  description: string;
}

export function CapabilityTemplateLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setRoot, rootStyle } = useScreenLock();

  /** The template being edited is the URL's, and only the URL's. */
  const templateId = searchParams.get('template');

  const [context, setContext] = useState<ErpContext | null>(null);
  const [templates, setTemplates] = useState<CapabilityTemplate[]>([]);
  const [detail, setDetail] = useState<CapabilityTemplateDetailPayload | null>(null);
  const [serverBalance, setServerBalance] = useState<WeightBalanceReport | null>(null);

  /** Weights in TENTHS, re-seeded from every server response. */
  const [weights, setWeights] = useState<Record<string, number>>({});
  /** The cell being typed in — raw text, so "4." and "" are expressible. */
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  /*
   * TWO ERRORS, NOT ONE. The library list and the open template load independently and one must not
   * clear the other's message: a single `error` meant a failed template read could be wiped by the
   * list refresh landing a moment later, leaving the tree region rendering "This template is empty"
   * over a template that had simply failed to load.
   */
  const [listError, setListError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  const [search, setSearch] = useState('');

  const [newLine, setNewLine] = useState<{
    parent_line_id: string;
    name: string;
    code: string;
  } | null>(null);
  const [attrs, setAttrs] = useState<AttrForm | null>(null);
  const [attrsDirty, setAttrsDirty] = useState(false);

  /*
   * ── CREATING A CAPABILITY MODEL FROM A TEMPLATE ───────────────────────────────────────
   *
   * Owner, 2026-10-07: *"Normally, I would create a Capability Model from Capability Templates
   * - that is the common process"*, and *"So Capability templates would be used to create
   * Capability Model"*.
   *
   * The path already existed in the other direction only: the capability model screen's New
   * Model panel offers a "Start from a template" picker. That is the same operation reached
   * from the wrong end — somebody standing in the library looking at the structure they want
   * had no way to act on it, and had to go to another screen and find it again in a dropdown.
   * Both paths now exist and both call the SAME endpoint with the same body; nothing was
   * removed to add this one.
   *
   * `null` when the panel is closed. `templateName` is carried so the panel can name what it
   * is copying without re-reading the row.
   */
  const [fromTemplate, setFromTemplate] = useState<{
    templateId: string;
    templateName: string;
    initiativeId: string;
    modelKey: string;
    name: string;
    description: string;
  } | null>(null);
  /** Loaded when that panel opens, because a model must belong to an initiative. */
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  /*
   * A PANEL'S REFUSAL BELONGS IN THE PANEL. The toast is pinned bottom-right and `FormPanel` is
   * anchored to the right edge above it, so a message raised while a panel is open used to be
   * painted over — it rendered, and looked like the button doing nothing. The toast now sits
   * above the panel as well, but a message about a FIELD still belongs beside the field rather
   * than in the opposite corner.
   */
  const [panelError, setPanelError] = useState<string | null>(null);

  /*
   * ── EDITING ONE ROW OF THE TREE ───────────────────────────────────────────────────────
   *
   * Owner, 2026-10-07: *"but on each capability add also capability to change description via
   * icon change"*. The tree edits a name inline on blur and could reach nothing else, so a
   * description — the one field that is a paragraph rather than a label — had no way in.
   *
   * A panel rather than a wider cell: §6.6's three writable fields are name, code and
   * description, and a textarea belongs in a form.
   */
  const [lineEdit, setLineEdit] = useState<{
    id: string;
    name: string;
    code: string;
    description: string;
  } | null>(null);
  const [lineEditDirty, setLineEditDirty] = useState(false);

  /*
   * ── CLONING A WHOLE TEMPLATE ──────────────────────────────────────────────────────────
   *
   * Owner, 2026-10-07: *"We also need to have ability to clone entire capability templates"*.
   *
   * The clone is a SERVER operation (§6.3a) — one transaction that copies the template and
   * every line with its weights. This panel only collects the two things the copy cannot
   * inherit: a `template_key`, which is unique across the install, and a name.
   */
  const [cloning, setCloning] = useState<{
    sourceId: string;
    sourceName: string;
    templateKey: string;
    name: string;
    /** What the two fields opened with, so "dirty" can mean "moved off the seed". */
    seedKey: string;
    seedName: string;
  } | null>(null);

  /* auto-dismiss toast — `FrameworksList.tsx:214-219`, 3000 ms. */
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const notifyOk = (message: string) => setToast({ type: 'success', message });
  const notifyErr = (message: string) => setToast({ type: 'error', message });

  /* 1 — who the user is, so the controls can be omitted rather than disabled (§17.3). */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const payload = await erpApi.context();
        if (!cancelled) setContext(payload.context);
      } catch {
        /* A context that cannot be read leaves the screen READ-ONLY, which is the safe default:
           every write is re-checked by the server anyway (§1.8, "the UI check is a convenience,
           never the boundary"). The list's own error state covers a server that is simply down. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * 2 — the library.
   *
   * Unfiltered on the wire — `?q=` exists (§6.1) and is deliberately not used: filtering one list
   * two different ways is how the two disagree. The library is one install's own standard
   * structures, so 200 rows is the whole of it; the page size is explicit rather than left to the
   * server's default of 50.
   */
  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const payload = await erpApi.capabilityTemplates.list({ limit: 200 });
      setTemplates(payload.capability_templates);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load the template library');
    } finally {
      setLoading(false);
    }
  }, []);

  /* Re-read when a template is opened or closed: a line write changes `line_count` and
     `weights_balanced`, and those two are what the table is for. */
  useEffect(() => {
    void loadList();
  }, [loadList, templateId]);

  const seedFromDetail = useCallback((payload: CapabilityTemplateDetailPayload) => {
    setDetail(payload);
    setServerBalance(payload.weightBalance ?? null);
    setWeights(
      Object.fromEntries(payload.capability_template_lines.map((l) => [l.id, l.weight_tenths])),
    );
    setEditing(null);
  }, []);

  /* 3 — one template and its lines. */
  const loadDetail = useCallback(async () => {
    if (!templateId) {
      setDetail(null);
      setServerBalance(null);
      setWeights({});
      return;
    }
    setDetailLoading(true);
    setDetailError(null);
    try {
      seedFromDetail(await erpApi.capabilityTemplates.get(templateId));
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Failed to load this template');
    } finally {
      setDetailLoading(false);
    }
  }, [templateId, seedFromDetail]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const canEdit = canManageTemplateLibrary(context);
  /*
   * Creating a model is a DIFFERENT permission from maintaining the library: §6.8 takes
   * `erp_administrator` or `erp_lead_architect`. A lead architect who may not edit templates
   * may still build a model from one, so the two flags are separate and the control is
   * omitted rather than disabled (§17.3).
   */
  const canCreateModel = canEditCapabilityModel(context);
  const template = detail?.capability_template ?? null;

  /**
   * `useMemo` so the empty case is a STABLE array: `detail?.capability_template_lines ?? []` builds
   * a new `[]` every render, and the live balance, the tree rows and the parent picker all depend
   * on it.
   */
  const lines = useMemo(() => detail?.capability_template_lines ?? [], [detail]);

  /** The tree as the weight arithmetic sees it, with local edits applied. */
  const nodes: WeightNode[] = useMemo(
    () =>
      lines.map((line) => ({
        id: line.id,
        parentId: line.parent_line_id,
        name: line.name,
        weightTenths: weights[line.id] ?? line.weight_tenths,
        sortOrder: line.sort_order,
      })),
    [lines, weights],
  );

  /**
   * THE LIVE BALANCE, computed locally as the user types — no round trip (§3.1: *"Client: runs the
   * package as the user types, to show the live running total"*).
   *
   * `describeBalance` is the same function the server runs on every weight write, so the sentences
   * here are byte-identical to the ones in a refusal.
   */
  const liveBalance = useMemo(() => describeBalance(nodes), [nodes]);

  /**
   * The rows `WeightedTree` renders. `depth` and `childCount` are derived from the tree the server
   * sent — unlike a capability, a template line carries neither, and both are a property of the
   * shape rather than a stored figure, so there is nothing here that could disagree with the
   * server. `effectiveWeightTenths` is `null` because §6.2 computes none, and `weightLocked` is
   * `false` because `capability_template_line` has no such column.
   */
  const treeRows: WeightedTreeRow[] = useMemo(() => {
    const children = new Map<string | null, number>();
    const byId = new Map<string, CapabilityTemplateLine>();
    for (const line of lines) {
      children.set(line.parent_line_id, (children.get(line.parent_line_id) ?? 0) + 1);
      byId.set(line.id, line);
    }
    const depthOf = (line: CapabilityTemplateLine): number => {
      let depth = 0;
      let parent = line.parent_line_id;
      /* A cycle cannot exist — §6.6 refuses one with `capability_template_line_cycle` — but the
         loop is bounded by the row count anyway, so a corrupt payload cannot hang the render. */
      while (parent !== null && depth <= lines.length) {
        depth += 1;
        parent = byId.get(parent)?.parent_line_id ?? null;
      }
      return depth;
    };
    return lines.map((line) => ({
      id: line.id,
      parentId: line.parent_line_id,
      code: line.code,
      name: line.name,
      weightTenths: line.weight_tenths,
      weightLocked: false,
      sortOrder: line.sort_order,
      depth: depthOf(line),
      childCount: children.get(line.id) ?? 0,
      effectiveWeightTenths: null,
    }));
  }, [lines]);

  /* ── Writes ───────────────────────────────────────────────────────────── */

  /**
   * Re-seed from a line write's response.
   *
   * §6.6 and §6.7 return ALL the lines and the new deficit rather than the one row that changed,
   * *"so the editor re-renders from one authoritative read and sees the new deficit immediately"*.
   * The template row itself has not changed, so it is carried over.
   */
  const applyLines = (payload: CapabilityTemplateLinesPayload) => {
    if (!template) return;
    seedFromDetail({
      capability_template: template,
      capability_template_lines: payload.capability_template_lines,
      weightBalance: payload.weightBalance,
    });
  };

  /** §6.7 — the ONE weight write. An unbalanced set saves; the response carries the deficit. */
  const putWeights = async (changes: readonly WeightChange[]) => {
    if (!templateId || !template || changes.length === 0) return;
    setBusy(true);
    try {
      applyLines(await erpApi.capabilityTemplates.putWeights(templateId, [...changes]));
    } catch (err) {
      /* The server's own wording (§1.6), and the local state is put back from the server so the
         screen never shows a weight it refused. */
      notifyErr(err instanceof Error ? err.message : 'Could not save that weight');
      await loadDetail();
    } finally {
      setBusy(false);
    }
  };

  /**
   * TYPING MOVES THE TOTAL, and nothing is saved here — the write happens on blur, so a half-typed
   * number never reaches the server.
   *
   * An out-of-range number is kept OUT of the running total and still SENT: `checkBalance` throws
   * on anything outside `0..1000` (`units.ts`), which would take the screen down mid-render, while
   * §6.7 answers `weight_out_of_range` and the user reads the server's own refusal. Clamping here
   * would hide a typo the server is about to name.
   */
  const typeWeight = (row: WeightedTreeRow, text: string) => {
    setEditing({ id: row.id, text });
    const tenths = textToTenths(text);
    if (tenths === null) return;
    if (!isWeightInRange(tenths)) return;
    setWeights((prev) => ({ ...prev, [row.id]: tenths }));
  };

  const commitWeight = async (row: WeightedTreeRow, text: string) => {
    setEditing(null);
    const tenths = textToTenths(text);
    if (tenths === null) {
      /* Not a number: put the cell back. Only unparseable text is dropped here. */
      setWeights((prev) => ({ ...prev, [row.id]: row.weightTenths }));
      return;
    }
    if (tenths === row.weightTenths) return;
    setWeights((prev) => ({ ...prev, [row.id]: tenths }));
    /* `capability_id` over a table of LINES is the server's own choice, not a mistranslation:
       `weights/write.ts:76-84` accepts either key so one client shape fits both endpoints. */
    await putWeights([{ capability_id: row.id, weight_tenths: tenths }]);
  };

  const renameLine = async (row: WeightedTreeRow, name: string) => {
    if (!templateId || !template || name.trim() === row.name || !name.trim()) return;
    setBusy(true);
    try {
      applyLines(
        await erpApi.capabilityTemplates.patchLine(templateId, row.id, { name: name.trim() }),
      );
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Could not rename that line');
      await loadDetail();
    } finally {
      setBusy(false);
    }
  };

  /** §6.6 — no weight is sent: a new line starts at 0.0%, and sending one is refused outright. */
  const addLine = async () => {
    if (!templateId || !template || !newLine) return;
    if (!newLine.name.trim()) {
      notifyErr('Name is required');
      return;
    }
    setBusy(true);
    try {
      applyLines(
        await erpApi.capabilityTemplates.addLine(templateId, {
          parent_line_id: newLine.parent_line_id || null,
          code: newLine.code.trim() || null,
          name: newLine.name.trim(),
          description: null,
          sort_order: 0,
        }),
      );
      setNewLine(null);
      notifyOk('Added at 0.0% — the group is now under by that much');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Could not add that line');
    } finally {
      setBusy(false);
    }
  };

  /** §6.6 — children cascade and the freed weight is left unallocated, never redistributed. */
  const deleteLine = async (row: WeightedTreeRow) => {
    if (!templateId || !template) return;
    /* `window.confirm`, as the capability tree does it in the same half of this module
       (`FrameworkBuilder.tsx:647`, `:728`, `:771`). */
    if (
      !window.confirm(
        `Delete “${row.name}”? Everything beneath it goes too, and its weight is left unallocated rather than shared out.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      applyLines(await erpApi.capabilityTemplates.removeLine(templateId, row.id));
      notifyOk('Deleted. Its weight is now unallocated.');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Could not delete that line');
    } finally {
      setBusy(false);
    }
  };

  /**
   * §6.3 and §6.4 — the template's OWN attributes, in a right-side panel (D-017).
   *
   * One panel does both, because the fields are the same four and the server takes the same four
   * either way; which it is, is whether `id` is set. The primary names what it writes — Create
   * Template or Save Template — and carries the Save icon, because this screen also writes lines
   * and weights and a button saying only "Save" would not say which.
   */
  const saveAttrs = async () => {
    if (!attrs) return;
    if (!attrs.name.trim()) {
      notifyErr('Name is required');
      return;
    }
    if (!attrs.template_key.trim()) {
      notifyErr('Key is required');
      return;
    }
    const body = {
      template_key: attrs.template_key.trim(),
      name: attrs.name.trim(),
      description: attrs.description.trim() || null,
    };
    setBusy(true);
    try {
      if (attrs.id === null) {
        const created = await erpApi.capabilityTemplates.create(body);
        setAttrs(null);
        setAttrsDirty(false);
        await loadList();
        /* Straight into the new template's tree: an empty template is not the end of the task,
           and the next thing to do is add a functional area to it. */
        setSearchParams({ template: created.capability_template.id });
        notifyOk('Template created');
        return;
      }
      await erpApi.capabilityTemplates.patch(attrs.id, body);
      setAttrs(null);
      setAttrsDirty(false);
      await loadList();
      if (templateId === attrs.id) await loadDetail();
      notifyOk('Template saved');
    } catch (err) {
      /* §6.3/§6.4's `409 template_key_in_use` names the clash, and it is shown as written. */
      notifyErr(err instanceof Error ? err.message : 'Could not save this template');
    } finally {
      setBusy(false);
    }
  };

  /**
   * §6.5 — DELETE, and the server has an endpoint for it, unlike a capability model.
   *
   * Its lines cascade. It is REFUSED with `409 capability_template_in_use` where any model was
   * created from it, because a model records which template it came from and that provenance is
   * part of what its scores were computed against. The row knows `model_count`, so the control is
   * not offered in that case and its reason is on the row; the server refuses independently, which
   * is the half that decides.
   */
  const deleteTemplate = async (row: CapabilityTemplate) => {
    if (
      !window.confirm(
        `Delete “${row.name}”? Its ${row.line_count} row${row.line_count === 1 ? '' : 's'} go with it. Models already created from a template are never affected — they hold their own copy.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await erpApi.capabilityTemplates.remove(row.id);
      await loadList();
      if (templateId === row.id) setSearchParams({});
      notifyOk('Template deleted');
    } catch (err) {
      if (err instanceof ErpApiError) {
        /* `capability_template_in_use` carries `modelCount` and a sentence naming it. */
        notifyErr(err.message);
        return;
      }
      notifyErr(err instanceof Error ? err.message : 'Could not delete this template');
    } finally {
      setBusy(false);
    }
  };

  const openCreate = () =>
    setAttrs({ id: null, template_key: '', name: '', description: '' });

  const openEdit = (
    row: CapabilityTemplate | CapabilityTemplateDetailPayload['capability_template'],
  ) => {
    setAttrs({
      id: row.id,
      template_key: row.template_key,
      name: row.name,
      description: row.description ?? '',
    });
    setAttrsDirty(false);
  };


  /*
   * ── OPEN THE "create a model from this template" PANEL ────────────────────────────────
   *
   * The initiative list is loaded HERE rather than with the screen, because it is needed only
   * by this panel and the library is otherwise readable without it. A list that cannot be read
   * leaves the panel open with no initiative to choose, which its own disabled Create and its
   * empty-state message explain — the alternative, refusing to open, hides the reason.
   */
  const openFromTemplate = async (row: CapabilityTemplate) => {
    setFromTemplate({
      templateId: row.id,
      templateName: row.name,
      initiativeId: '',
      /* Seeded from the template's name — an empty key field asks somebody to invent an
         identifier before they have been shown what one looks like. Editable, as a seed is. */
      modelKey: suggestKeyFromName(row.name),
      /* Seeded from the template's name, because a model built from one usually IS that
         structure for this initiative. It is a starting point, not a lock — every character is
         editable, and the key is deliberately NOT derived from it: the two identify different
         things and a key quietly generated from a name is a key nobody chose. */
      name: row.name,
      description: '',
    });
    setPanelError(null);
    try {
      const payload = await erpApi.initiatives.list({ limit: 200 });
      setInitiatives(payload.initiatives);
    } catch {
      setInitiatives([]);
    }
  };

  /*
   * THE SAME ENDPOINT THE MODEL SCREEN'S OWN PANEL CALLS, with the same body — §6.8 with
   * `from_capability_template_id`. Two ways in, one operation, so there is no second
   * implementation of the rules that matter: the server refuses an unbalanced template, an
   * initiative that is already baselined, and a key that clashes or is malformed.
   */
  const createModelFromTemplate = async () => {
    if (!fromTemplate) return;
    setPanelError(null);
    if (!fromTemplate.initiativeId) {
      setPanelError('Choose the initiative this model belongs to.');
      return;
    }
    /* NORMALISED, not refused — see `../keys.ts`. Only an input with nothing usable in it
       fails, and the sentence then says what is missing rather than restating the rules. */
    const modelKey = finishKey(fromTemplate.modelKey);
    const keyProblem = describeKeyProblem(fromTemplate.modelKey);
    if (keyProblem !== null) {
      setPanelError(keyProblem);
      return;
    }
    if (!fromTemplate.name.trim()) {
      setPanelError('Name is required.');
      return;
    }
    setBusy(true);
    try {
      const payload = await erpApi.capabilityModels.create(fromTemplate.initiativeId, {
        model_key: modelKey,
        name: fromTemplate.name.trim(),
        description: fromTemplate.description.trim() || null,
        from_capability_template_id: fromTemplate.templateId,
      });
      setFromTemplate(null);
      /*
       * STRAIGHT INTO THE MODEL THAT WAS JUST MADE. The task is not "a model exists", it is
       * "this initiative has its capability model" — and the copied tree is the thing to look
       * at next. Same reasoning as creating a template landing in its own tree above.
       */
      navigate(
        `/erp_consolidation/capability-model?initiative=${encodeURIComponent(
          fromTemplate.initiativeId,
        )}&model=${encodeURIComponent(payload.capability_model.id)}`,
      );
    } catch (err) {
      setPanelError(
        err instanceof Error ? err.message : 'Could not create a model from that template.',
      );
    } finally {
      setBusy(false);
    }
  };

  /** Seeded from the line itself, so an untouched panel saves the values already stored. */
  const openLineEdit = (row: WeightedTreeRow) => {
    const line = lines.find((candidate) => candidate.id === row.id);
    setPanelError(null);
    setLineEdit({
      id: row.id,
      name: line?.name ?? row.name,
      code: line?.code ?? row.code ?? '',
      description: line?.description ?? '',
    });
    setLineEditDirty(false);
  };

  const saveLineEdit = async () => {
    if (!lineEdit || !templateId) return;
    setPanelError(null);
    if (!lineEdit.name.trim()) {
      setPanelError('Name is required.');
      return;
    }
    setBusy(true);
    try {
      /* Blank code and blank description are `null`, not `''` — both columns are nullable and
         the tree tests for truthiness before it renders either. */
      applyLines(
        await erpApi.capabilityTemplates.patchLine(templateId, lineEdit.id, {
          name: lineEdit.name.trim(),
          code: lineEdit.code.trim() || null,
          description: lineEdit.description.trim() || null,
        }),
      );
      setLineEdit(null);
      setLineEditDirty(false);
      notifyOk('Saved');
    } catch (err) {
      setPanelError(err instanceof Error ? err.message : 'Could not save that change.');
    } finally {
      setBusy(false);
    }
  };

  const cloneTemplate = async () => {
    if (!cloning) return;
    setPanelError(null);
    const templateKey = cloning.templateKey.trim();
    if (!templateKey) {
      setPanelError('Key is required.');
      return;
    }
    if (!cloning.name.trim()) {
      setPanelError('Name is required.');
      return;
    }
    setBusy(true);
    try {
      const created = await erpApi.capabilityTemplates.clone(cloning.sourceId, {
        template_key: templateKey,
        name: cloning.name.trim(),
      });
      setCloning(null);
      await loadList();
      /* Straight into the copy's own tree, as creating a template already does: the next thing
         somebody does with a clone is change the part they cloned it to change. */
      setSearchParams({ template: created.capability_template.id });
      notifyOk('Template cloned');
    } catch (err) {
      /* §6.3a answers a duplicate key with `template_key_in_use`, named. */
      setPanelError(err instanceof Error ? err.message : 'Could not clone that template.');
    } finally {
      setBusy(false);
    }
  };

  /*
   * §6.6a — the whole sibling set, in its new order, in one call. No confirmation: a re-order
   * changes no weight and no parent, and it is undone by dragging the row back.
   */
  const reorderLines = async (parentId: string | null, orderedIds: readonly string[]) => {
    if (!templateId) return;
    setBusy(true);
    try {
      applyLines(await erpApi.capabilityTemplates.reorderLines(templateId, parentId, orderedIds));
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Could not re-order those rows');
      /* Put the screen back on the server's truth: the rows on screen have already moved
         optimistically through the tree's own render, and a refused write must not leave them
         sitting in an order the database does not have. */
      void loadDetail();
    } finally {
      setBusy(false);
    }
  };

  const changeAttr = (patch: Partial<AttrForm>) => {
    setAttrsDirty(true);
    setAttrs((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  /* ── Render ───────────────────────────────────────────────────────────── */

  const visible = templates.filter((row) => {
    const term = search.trim().toLowerCase();
    if (term === '') return true;
    return (
      row.name.toLowerCase().includes(term) ||
      row.template_key.toLowerCase().includes(term) ||
      (row.description ?? '').toLowerCase().includes(term)
    );
  });

  const balanced = liveBalance.balanced && lines.length > 0;
  const bandClass = lines.length === 0 ? ' blocked' : balanced ? ' ready' : '';

  return (
    <ErpScreen>
      {/* `fb-page` carries the `--fb-*` token set that `WeightedTree.css` declares on it — any
          screen rendering the weighted tree puts that class on its root. */}
      <div className="page fb-page" ref={setRoot} style={rootStyle}>
        <HelpPanel help={TemplateLibraryHelp} />
        <PageHeader
          title="Capability Templates"
          subtitle="Reusable capability structures a new capability model can be created from — copied into the model, never shared with it"
        />

        {templateId === null ? (
          <>
            {/* D-018's filter bar — `recordList.css`'s `.erp-rl-filter-bar`, which carries the gap,
                the wrap and the 20px below it, and never shrinks because it is part of the locked
                header region. */}
            <div className="erp-rl-filter-bar">
              {/* The pattern's search box: the `search` ligature INSIDE the field
                  (`KnowledgeLibrary.tsx:814-830`), and Clear as a ghost button carrying the `clear`
                  ligature (`:871-881`). Clear appears only once there is a term to clear, and it
                  clears the search term alone — a bar-level Clear would read as "clear every
                  filter", which is not what this control has ever done on this pattern. */}
              <div className="erp-rl-search">
                <i className="material-icons">search</i>
                <input
                  id="erp-ctl-search"
                  type="text"
                  className="erp-rl-input"
                  placeholder="Template name or key…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {search !== '' && (
                <button
                  type="button"
                  className="erp-rl-btn erp-rl-btn--ghost"
                  onClick={() => setSearch('')}
                >
                  <i className="material-icons">clear</i>
                  Clear
                </button>
              )}

              {canEdit && (
                <button
                  type="button"
                  className="erp-rl-btn erp-rl-btn--primary ctl-bar-end"
                  onClick={openCreate}
                >
                  <i className="material-icons">add</i>
                  New Template
                </button>
              )}
            </div>

            {loading ? (
              <div className="erp-rl-loading">
                <i className="material-icons erp-rl-spin">sync</i>
                Loading templates…
              </div>
            ) : listError ? (
              <div className="erp-rl-empty">
                <div className="erp-rl-empty-icon">
                  <i className="material-icons erp-rl-error-icon">error</i>
                </div>
                <h2 className="erp-rl-empty-title">{listError}</h2>
                <button
                  type="button"
                  className="erp-rl-btn erp-rl-btn--primary"
                  onClick={() => void loadList()}
                >
                  <i className="material-icons">sync</i>
                  Retry
                </button>
              </div>
            ) : templates.length === 0 ? (
              <div className="erp-rl-empty">
                <div className="erp-rl-empty-icon">
                  <i className="material-icons">schema</i>
                </div>
                <h2 className="erp-rl-empty-title">No capability templates yet</h2>
                <p className="erp-rl-empty-body">
                  A template is a reusable structure — functional areas, capabilities and their
                  weights — that a new capability model can be created from. Until there is one,
                  every model starts empty.
                </p>
                {canEdit && (
                  <button
                    type="button"
                    className="erp-rl-btn erp-rl-btn--primary"
                    style={{ marginTop: 12 }}
                    onClick={openCreate}
                  >
                    <i className="material-icons">add</i>
                    New Template
                  </button>
                )}
              </div>
            ) : visible.length === 0 ? (
              <div className="erp-rl-empty">
                <div className="erp-rl-empty-icon">
                  <i className="material-icons">search</i>
                </div>
                <h2 className="erp-rl-empty-title">No template matches this filter</h2>
                <p className="erp-rl-empty-body">
                  {templates.length} template{templates.length === 1 ? '' : 's'} in the library.
                </p>
              </div>
            ) : (
              /* `erp-rl-table-wrapper` IS the scroll region — `flex: 1 1 auto; min-height: 0;
                 overflow: auto` — so the header, the filters and the column headers stay put and
                 only the rows move. */
              <div className="erp-rl-table-wrapper">
                <table className="erp-rl-table ctl-cols">
                  <thead>
                    <tr>
                      <th>Template</th>
                      <th>Key</th>
                      {/* Headed as the models table heads the same figure. The field behind it is
                          `line_count`, which counts every row in the tree, functional areas
                          included — exactly as `capability_count` does there. */}
                      <th className="erp-rl-right">Capabilities</th>
                      <th>Weights</th>
                      <th className="erp-rl-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {/* The pattern's primary cell: the name at weight 500, its description
                              beneath at 12px muted. The name opens the template, which is the
                              row's own action. */}
                          <button
                            type="button"
                            className="erp-rl-name-btn"
                            title="Open this template's structure"
                            onClick={() => setSearchParams({ template: row.id })}
                          >
                            {row.name}
                          </button>
                          {row.description && (
                            <span className="erp-rl-desc">{row.description}</span>
                          )}
                        </td>
                        <td className="erp-rl-sec erp-rl-nowrap">{row.template_key}</td>
                        <td className="erp-rl-num">{row.line_count}</td>
                        <td>
                          {/* The server's own verdict, not one recomputed here (§3.3). */}
                          <span
                            className={`erp-rl-badge ${
                              row.weights_balanced
                                ? 'erp-rl-badge--success'
                                : 'erp-rl-badge--warning'
                            }`}
                            title={
                              row.weights_balanced
                                ? 'Every level totals exactly 100.0%, so a model can be created from it'
                                : 'At least one level does not total 100.0% — it saves, but a model cannot be created from it'
                            }
                          >
                            {row.weights_balanced ? 'Balanced' : 'Does not balance'}
                          </span>
                        </td>
                        <td>
                          <div className="erp-rl-actions">
                            {/*
                              CREATE A CAPABILITY MODEL FROM THIS TEMPLATE — the owner's
                              *"common process"*, so it leads the row rather than trailing it.
                              📋 is this module's established glyph for a capability model
                              (`InitiativesList.tsx:310`), and the action is the same operation
                              the model screen's own picker performs.

                              OFFERED ONLY WHERE THE SERVER WOULD ACCEPT IT, which is this
                              screen's existing rule for the bin: §6.8 refuses an unbalanced
                              template, so an unbalanced row says why instead of carrying a
                              control that fails on press.
                            */}
                            {canCreateModel &&
                              (row.weights_balanced && row.line_count > 0 ? (
                                <button
                                  type="button"
                                  className="erp-rl-glyph"
                                  title="Create a capability model from this template"
                                  aria-label={`Create a capability model from ${row.name}`}
                                  disabled={busy}
                                  onClick={() => void openFromTemplate(row)}
                                >
                                  📋
                                </button>
                              ) : (
                                <span
                                  className="erp-rl-sec ctl-no-instantiate"
                                  title={
                                    row.line_count === 0
                                      ? 'This template has no rows yet, so there is nothing to copy into a model.'
                                      : 'At least one level does not total 100.0%, so a model created from this template could not balance either.'
                                  }
                                >
                                  {row.line_count === 0 ? 'empty' : 'unbalanced'}
                                </span>
                              ))}
                            <button
                              type="button"
                              className="erp-rl-glyph erp-rl-glyph--view"
                              title="Open this template's structure"
                              aria-label={`Open ${row.name}`}
                              onClick={() => setSearchParams({ template: row.id })}
                            >
                              👁️
                            </button>
                            {canEdit && (
                              <button
                                type="button"
                                className="erp-rl-glyph"
                                title="Edit this template's name, key and description"
                                aria-label={`Edit ${row.name}`}
                                onClick={() => openEdit(row)}
                              >
                                ✏️
                              </button>
                            )}
                            {/* CLONE — structure and weights copied in one transaction (§6.3a).
                                Offered on any template, balanced or not: a clone of a draft you
                                are still building is exactly when it is most useful. */}
                            {canEdit && (
                              <button
                                type="button"
                                className="erp-rl-glyph"
                                title="Clone this template, with every functional area, capability and weight"
                                aria-label={`Clone ${row.name}`}
                                disabled={busy}
                                onClick={() =>
                                  setCloning({
                                    sourceId: row.id,
                                    sourceName: row.name,
                                    /* Seeded, not generated: a key has to be chosen, and these
                                       two suffixes are the obvious starting point rather than a
                                       decision made on the user's behalf. */
                                    templateKey: `${row.template_key}-copy`,
                                    name: `${row.name} (copy)`,
                                    seedKey: `${row.template_key}-copy`,
                                    seedName: `${row.name} (copy)`,
                                  })
                                }
                              >
                                📄
                              </button>
                            )}
                            {/* THE BIN IS OFFERED ONLY WHERE THE SERVER WOULD ACCEPT IT. §6.5
                                refuses a template any model was created from, and the row carries
                                the count, so the alternative is a control that fails on press. The
                                reason is said rather than left to be discovered. */}
                            {canEdit &&
                              (row.model_count === 0 ? (
                                <button
                                  type="button"
                                  className="erp-rl-glyph erp-rl-glyph--delete"
                                  title="Delete this template"
                                  aria-label={`Delete ${row.name}`}
                                  disabled={busy}
                                  onClick={() => void deleteTemplate(row)}
                                >
                                  🗑️
                                </button>
                              ) : (
                                <span
                                  className="erp-rl-sec ctl-no-delete"
                                  title={`${row.model_count} capability model${row.model_count === 1 ? ' was' : 's were'} created from this template, so it cannot be deleted — a model records which template its scores were computed against.`}
                                >
                                  in use by {row.model_count}
                                </span>
                              ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="ctl-toolbar">
              {/* Back to the library. The only way out of a template, so it is not optional. */}
              <button
                type="button"
                className="erp-rl-btn erp-rl-btn--ghost"
                onClick={() => setSearchParams({})}
              >
                <i className="material-icons">arrow_back</i>
                All templates
              </button>
              {canEdit && template !== null && (
                <button
                  type="button"
                  className="erp-rl-btn erp-rl-btn--ghost"
                  onClick={() => openEdit(template)}
                >
                  <i className="material-icons">edit</i>
                  Edit template
                </button>
              )}
            </div>

            {template && (
              /* The band naming what is being edited — the capability model editor's ribbon,
                 copied with its values rather than shared, and with no colour on it for the
                 reason it has none there (owner, 2026-10-05: "There should be no colors"). */
              <div className="ctl-ribbon">
                <span className="ctl-ribbon-name">{template.name}</span>
                <span className="ctl-ribbon-label">Key</span>
                <span className="ctl-ribbon-name">{template.template_key}</span>
              </div>
            )}

            {detailLoading ? (
              <div className="erp-rl-loading">
                <i className="material-icons erp-rl-spin">sync</i>
                Loading this template…
              </div>
            ) : detailError ? (
              <div className="erp-rl-empty">
                <div className="erp-rl-empty-icon">
                  <i className="material-icons erp-rl-error-icon">error</i>
                </div>
                <h2 className="erp-rl-empty-title">{detailError}</h2>
                <button
                  type="button"
                  className="erp-rl-btn erp-rl-btn--primary"
                  onClick={() => void loadDetail()}
                >
                  <i className="material-icons">sync</i>
                  Retry
                </button>
              </div>
            ) : template === null ? (
              /* Neither loading nor failed, and still nothing: the first render after the URL
                 changed, before the read lands. Saying "loading" is the only honest thing — saying
                 "this template is empty" would be a statement about a template nobody has read
                 yet. */
              <div className="erp-rl-loading">
                <i className="material-icons erp-rl-spin">sync</i>
                Loading this template…
              </div>
            ) : (
              <>
                {/*
                  THE BALANCE BAND — the model editor's gate strip without a gate.
                  Nothing is ever scored against a template, so there is no baseline to earn and no
                  action to withhold. What the band carries instead is the consequence §6.8 attaches
                  to the same arithmetic: an unbalanced template cannot be instantiated. Adding a
                  functional area is therefore the one action this band exists for, which is why it
                  is the filled primary (CLAUDE.md §1.9.3.1 — one filled primary per row).
                */}
                <div className={`ctl-band${bandClass}`}>
                  <div className="ctl-band__text">
                    <div className="ctl-band__title">
                      {lines.length === 0
                        ? 'This template is empty'
                        : balanced
                          ? 'Every level totals exactly 100.0%'
                          : /* The server's own envelope sentence, from the same formatter. */
                            summariseBalance(liveBalance.groups)}
                    </div>
                    <div className="ctl-band__sub">
                      {lines.length === 0
                        ? 'Add a functional area to start. A model cannot be created from an empty template.'
                        : balanced
                          ? 'A capability model can be created from this template. Its rows and weights are copied into the model, never shared with it.'
                          : 'It saves as it is — but a model cannot be created from it until every level totals exactly 100.0%, because the model it produced could not balance either.'}
                    </div>
                    {!balanced && liveBalance.groups.length > 0 && (
                      /* Listed, not counted, and in tree order — "'12 skipped' tells you something
                         is wrong, not which line to fix" (`ApplicationMasterDataTab.tsx:803-804`). */
                      <ul className="ctl-band__groups">
                        {liveBalance.groups.map((group) => (
                          <li key={group.parentCapabilityId ?? 'functional-areas'}>
                            {group.parentName ?? 'Functional areas'}: {group.message}
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* The server's own refusal, when it has spoken. Kept beside the live reading
                        rather than replacing it: both come from one formatter, so a difference
                        between them would itself be worth seeing. */}
                    {serverBalance &&
                      !serverBalance.balanced &&
                      serverBalance.groups.length > 0 && (
                        <ul className="ctl-band__groups">
                          {serverBalance.groups.map((group) => (
                            <li key={`${group.parentCapabilityId ?? 'root'}-server`}>
                              {group.parentName ?? 'Functional areas'}: {group.message}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                  <div className="ctl-band__actions">
                    {canEdit && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={busy}
                        onClick={() => setNewLine({ parent_line_id: '', name: '', code: '' })}
                      >
                        + Functional area
                      </button>
                    )}
                  </div>
                </div>

                {lines.length === 0 ? (
                  <div className="ctl-state">
                    <p>No functional areas yet in {template?.name ?? 'this template'}.</p>
                    <p className="ctl-hint">
                      Add the top level first — each functional area then holds the capabilities
                      beneath it, and every level must total exactly 100.0%.
                    </p>
                  </div>
                ) : (
                  <WeightedTree
                    rows={treeRows}
                    weights={weights}
                    editing={editing}
                    editable={canEdit}
                    busy={busy}
                    itemColumnLabel="Capability"
                    itemNoun={{ one: 'capability', many: 'capabilities' }}
                    rootGroupLabel="Functional areas"
                    addChildLabel="+ capability"
                    /* §6.2 computes no effective weight, and §3.3 makes that figure the server's,
                       so the column is absent rather than derived here. */
                    showEffective={false}
                    onTypeWeight={typeWeight}
                    onCommitWeight={(row, text) => void commitWeight(row, text)}
                    onRename={(row, name) => void renameLine(row, name)}
                    /* NO `onToggleLock`: `capability_template_line` has no `weight_locked` column
                       (`0094`), so there is nothing to store and no padlock is drawn. */
                    onAddChild={(row) => setNewLine({ parent_line_id: row.id, name: '', code: '' })}
                    onEditRow={openLineEdit}
                    onReorder={(parentId, orderedIds) => void reorderLines(parentId, orderedIds)}
                    onDelete={(row) => void deleteLine(row)}
                    onApplyWeights={(changes) => void putWeights(changes)}
                    onError={notifyErr}
                  />
                )}
              </>
            )}
          </>
        )}


        {/*
          CREATE A CAPABILITY MODEL FROM THIS TEMPLATE — the owner's *"common process"*, reached
          from the template rather than from the model screen. A right-side panel at the single
          width (D-017, `../chrome/panelWidths.css`), like every other form in this module.

          THREE FIELDS AND NOTHING ELSE, because three is what §6.8 needs: the initiative the
          model belongs to, its key, and its name. The structure comes from the template, which
          is the point of starting here.
        */}
        {fromTemplate && (
          <FormPanel
            open
            title="Create Capability Model"
            subtitle={`From ${fromTemplate.templateName}`}
            onRequestClose={() => {
              setFromTemplate(null);
              setPanelError(null);
            }}
            onSave={() => void createModelFromTemplate()}
            busy={busy}
            saveLabel="Create Model"
            busyLabel="Creating…"
            /* The name is seeded, so "dirty" cannot mean "anything typed" — it means moved off
               what the panel opened with. */
            dirty={
              fromTemplate.initiativeId !== '' ||
              fromTemplate.modelKey.trim() !== '' ||
              fromTemplate.name.trim() !== fromTemplate.templateName ||
              fromTemplate.description.trim() !== ''
            }
            discardMessage="Discard this new capability model?"
          >
            {panelError && (
              <p className="fb-panel-error" role="alert">
                {panelError}
              </p>
            )}

            <div className="form-group">
              {/* D-014 — an initiative is a RECORD, so this is the platform's record-picker
                  field, with this module's own parameters. */}
              <SelectField
                label="Initiative"
                id="erp-ctl-model-initiative"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                placeholder={ERP_PROMPT.initiative}
                allowClear={false}
                value={fromTemplate.initiativeId}
                options={initiatives.map((row) => ({
                  value: row.id,
                  id: row.name,
                  description: row.description ?? undefined,
                }))}
                onChange={(value) =>
                  setFromTemplate((prev) => (prev ? { ...prev, initiativeId: value } : prev))
                }
              />
              {initiatives.length === 0 && (
                <p className="ctl-hint">
                  There are no initiatives to put a model in yet. A capability model belongs to one
                  consolidation exercise, so an initiative has to exist first.
                </p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-model-key">Key *</label>
              <input
                id="erp-ctl-model-key"
                type="text"
                value={fromTemplate.modelKey}
                /* Tidied on the way in: capitals lower-case themselves, spaces and underscores
                   become hyphens, anything else is dropped. The EDGES are left alone until the
                   value is used, or a trailing hyphen could never be followed by anything. */
                onChange={(e) =>
                  setFromTemplate((prev) =>
                    prev ? { ...prev, modelKey: draftKey(e.target.value) } : prev,
                  )
                }
                onBlur={() =>
                  setFromTemplate((prev) =>
                    prev ? { ...prev, modelKey: finishKey(prev.modelKey) } : prev,
                  )
                }
                placeholder="e.g., fy27-scm"
                required
              />
              <p className="ctl-hint">
                Suggested from the template&rsquo;s name &mdash; change it to whatever this model
                should be called. Capitals and spaces are tidied as you type, so
                &ldquo;FY27 SCM&rdquo; becomes <code>fy27-scm</code>. Unique within the
                initiative, and <strong>every version of this model keeps it</strong>.
              </p>
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-model-name">Name *</label>
              <input
                id="erp-ctl-model-name"
                type="text"
                value={fromTemplate.name}
                onChange={(e) =>
                  setFromTemplate((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
                placeholder="e.g., FY27 Capability Model"
                required
              />
              <p className="ctl-hint">
                Seeded from the template&rsquo;s name. Change it to whatever this initiative calls
                its model &mdash; the two are not kept in step afterwards.
              </p>
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-model-description">Description</label>
              <textarea
                id="erp-ctl-model-description"
                rows={3}
                value={fromTemplate.description}
                onChange={(e) =>
                  setFromTemplate((prev) =>
                    prev ? { ...prev, description: e.target.value } : prev,
                  )
                }
                placeholder="What this model covers, and what it is for"
              />
            </div>
            <p className="ctl-hint">
              Every functional area, capability and weight in{' '}
              <strong>{fromTemplate.templateName}</strong> is <strong>copied</strong> into the new
              model. Editing the model afterwards never touches this template, and editing this
              template never reaches the model.
            </p>
          </FormPanel>
        )}



        {/* CLONE — two fields, because the copy inherits everything else. */}
        {cloning && (
          <FormPanel
            open
            title="Clone Capability Template"
            subtitle={`From ${cloning.sourceName}`}
            onRequestClose={() => {
              setCloning(null);
              setPanelError(null);
            }}
            onSave={() => void cloneTemplate()}
            busy={busy}
            saveLabel="Clone Template"
            busyLabel="Cloning…"
            /* Both fields open seeded, so dirty means moved off the seed — not "anything typed",
               which would be true the instant the panel opened. */
            dirty={
              cloning.templateKey !== cloning.seedKey || cloning.name !== cloning.seedName
            }
            discardMessage="Discard this clone?"
          >
            {panelError && (
              <p className="fb-panel-error" role="alert">
                {panelError}
              </p>
            )}

            <div className="form-group">
              <label htmlFor="erp-ctl-clone-key">Key *</label>
              <input
                id="erp-ctl-clone-key"
                type="text"
                value={cloning.templateKey}
                onChange={(e) =>
                  setCloning((prev) => (prev ? { ...prev, templateKey: e.target.value } : prev))
                }
                required
              />
              <p className="ctl-hint">
                Unique across this install. Seeded from the original with <code>-copy</code>{' '}
                appended, which is a starting point rather than a decision &mdash; change it to
                whatever this copy is actually for.
              </p>
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-clone-name">Name *</label>
              <input
                id="erp-ctl-clone-name"
                type="text"
                value={cloning.name}
                onChange={(e) =>
                  setCloning((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
                required
              />
            </div>
            <p className="ctl-hint">
              Every functional area, capability, code, description and <strong>weight</strong> is
              copied. The weights come across <strong>unchanged</strong>, so a clone of a balanced
              template balances and a clone of an unbalanced one is unbalanced in the same places
              &mdash; nothing is redistributed to make a sum come out.
            </p>
            <p className="ctl-hint">
              The two templates are independent from the moment the copy exists: editing either one
              never reaches the other.
            </p>
          </FormPanel>
        )}

        {/* ONE ROW OF THE TREE — the ✏️ beside it. Name, code and description are §6.6's three
            writable fields, and the description is the reason this panel exists. */}
        {lineEdit && (
          <FormPanel
            open
            title="Edit Capability"
            subtitle={template?.name}
            onRequestClose={() => {
              setLineEdit(null);
              setLineEditDirty(false);
              setPanelError(null);
            }}
            onSave={() => void saveLineEdit()}
            busy={busy}
            saveLabel="Save"
            busyLabel="Saving…"
            dirty={lineEditDirty}
            discardMessage="Discard these changes?"
          >
            {panelError && (
              <p className="fb-panel-error" role="alert">
                {panelError}
              </p>
            )}

            <div className="form-group">
              <label htmlFor="erp-ctl-line-edit-name">Name *</label>
              <input
                id="erp-ctl-line-edit-name"
                type="text"
                value={lineEdit.name}
                onChange={(e) => {
                  setLineEditDirty(true);
                  setLineEdit((prev) => (prev ? { ...prev, name: e.target.value } : prev));
                }}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-line-edit-code">Code</label>
              <input
                id="erp-ctl-line-edit-code"
                type="text"
                value={lineEdit.code}
                onChange={(e) => {
                  setLineEditDirty(true);
                  setLineEdit((prev) => (prev ? { ...prev, code: e.target.value } : prev));
                }}
                placeholder="e.g., O2C"
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-line-edit-description">Description</label>
              <textarea
                id="erp-ctl-line-edit-description"
                rows={4}
                value={lineEdit.description}
                onChange={(e) => {
                  setLineEditDirty(true);
                  setLineEdit((prev) => (prev ? { ...prev, description: e.target.value } : prev));
                }}
                placeholder="What this capability covers"
              />
              <p className="ctl-hint">
                The description travels with this row when a capability model is created from the
                template &mdash; it is copied, like the name, the code and the weight.
              </p>
            </div>
            <p className="ctl-hint">
              The <strong>weight</strong> is not here: it is typed in the tree itself, so the
              sibling total updates as you change it and the 100% rule has one place it lives.
            </p>
          </FormPanel>
        )}

        {/* A new row. A RIGHT-SIDE PANEL at the single width, not a window — D-017, and the panel
            does not dim the screen, so the tree this row is being added into stays readable while
            it is being filled in. */}
        {newLine && (
          <FormPanel
            open
            title={newLine.parent_line_id ? 'New Capability' : 'New Functional Area'}
            subtitle={template?.name}
            onRequestClose={() => setNewLine(null)}
            onSave={() => void addLine()}
            busy={busy}
            saveLabel="Add"
            busyLabel="Adding…"
            /* Anything typed counts. The parent is NOT part of this test: it is seeded from the row
               the + was clicked on, so it is where the panel started rather than an edit. */
            dirty={newLine.name.trim() !== '' || newLine.code.trim() !== ''}
            discardMessage="Discard this new row?"
          >
            <div className="form-group">
              <label htmlFor="erp-ctl-line-name">Name *</label>
              <input
                id="erp-ctl-line-name"
                type="text"
                value={newLine.name}
                onChange={(e) =>
                  setNewLine((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
                placeholder="e.g., Order to Cash"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-line-code">Code</label>
              <input
                id="erp-ctl-line-code"
                type="text"
                value={newLine.code}
                onChange={(e) =>
                  setNewLine((prev) => (prev ? { ...prev, code: e.target.value } : prev))
                }
                placeholder="e.g., O2C"
              />
            </div>
            <div className="form-group">
              {/* D-014 — a parent is a RECORD, so this is the platform's record-picker field, with
                  the in-module precedent's own parameters (`BusinessUnitHierarchy.tsx:612`;
                  `width="full"`, `orientation="stacked"`, and the `display: flex` that makes
                  `full` mean full inside a block `.form-group` — UI_GAPS §11). */}
              <SelectField
                label="Parent"
                id="erp-ctl-line-parent"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                value={newLine.parent_line_id}
                /* '' IS A REAL CHOICE here, with its own wording — "this is a functional area" —
                   so there is no clear row: a second blank row would duplicate it, and the prompt
                   row would carry `UNSET` into `parent_line_id`. */
                allowClear={false}
                options={[
                  { value: '', label: ERP_PROMPT.noParentFunctionalArea },
                  ...orderTree(nodes).map((node) => ({ value: node.id, id: node.name })),
                ]}
                onChange={(value) =>
                  setNewLine((prev) => (prev ? { ...prev, parent_line_id: value } : prev))
                }
              />
              <p className="ctl-hint">
                A new row starts at <strong>0.0%</strong>. Nothing is taken from its siblings to pay
                for it, so this group will read as under by that much until you allocate.
              </p>
            </div>
          </FormPanel>
        )}

        {/* The template's own attributes — create and edit, in one panel at the single width
            (`../chrome/panelWidths.css`). The primary says what it writes and carries the Save
            icon, which is D-017's own instruction: *"Save should be icon Save"*. */}
        {attrs !== null && (
          <FormPanel
            open
            title={
              attrs.id === null
                ? 'New Capability Template'
                : attrs.name.trim() === ''
                  ? 'Capability Template'
                  : attrs.name
            }
            subtitle={attrs.id === null ? undefined : attrs.template_key}
            onRequestClose={() => {
              setAttrs(null);
              setAttrsDirty(false);
            }}
            onSave={() => void saveAttrs()}
            busy={busy}
            saveLabel={attrs.id === null ? 'Create Template' : 'Save Template'}
            busyLabel={attrs.id === null ? 'Creating…' : 'Saving…'}
            dirty={
              attrs.id === null
                ? attrs.name.trim() !== '' || attrs.template_key.trim() !== ''
                : attrsDirty
            }
            discardMessage={
              attrs.id === null
                ? 'Discard this new template?'
                : 'Discard your changes to this template?'
            }
          >
            <div className="form-group">
              <label htmlFor="erp-ctl-name">Name *</label>
              <input
                id="erp-ctl-name"
                type="text"
                required
                value={attrs.name}
                onChange={(e) => changeAttr({ name: e.target.value })}
                placeholder="e.g., SAP S/4 Core"
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-key">Key *</label>
              <input
                id="erp-ctl-key"
                type="text"
                required
                value={attrs.template_key}
                onChange={(e) => changeAttr({ template_key: e.target.value })}
                placeholder="e.g., sap-s4-core"
              />
              <p className="ctl-hint">
                How this template is referred to, and{' '}
                <strong>no two templates may share one</strong>. Saving a key another template
                already holds is refused, and the refusal names the key.
              </p>
            </div>
            <div className="form-group">
              <label htmlFor="erp-ctl-description">Description</label>
              <textarea
                id="erp-ctl-description"
                rows={3}
                value={attrs.description}
                onChange={(e) => changeAttr({ description: e.target.value })}
                placeholder="What this structure is for, and where it came from"
              />
            </div>
            <div className="form-group">
              <p className="ctl-hint">
                There is no <strong>Active</strong> flag. A template you no longer want is
                deleted, which is possible only while no capability model was ever created from
                it &mdash; a model records which template its scores were computed against.
              </p>
            </div>
          </FormPanel>
        )}

        {toast && (
          <div className={`ctl-toast ctl-toast-${toast.type}`} role="status">
            {toast.type === 'success' ? '✓ ' : '⚠ '}
            {toast.message}
          </div>
        )}
      </div>
    </ErpScreen>
  );
}
